import React, { useContext, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Dna } from "react-loader-spinner";

import { Paper } from "@mui/material";
import Layout from "components/Layout";
import { LangContext } from "context/LangContext";
import loginSchema from "formValidationSchemas/loginSchema";
import registrationSchema from "formValidationSchemas/registrationSchema";
import forgotPasswordSchema from "formValidationSchemas/forgotPasswordSchema";
import LoginForm from "components/LoginForm";
import RegistrationForm from "components/RegistrationForm";
import ForgotPasswordForm from "components/ForgotPasswordForm";
import AlertMessage from "components/forms/AlertMessage";

import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { useUser } from "utils/UserContext";
import { auth } from '../../firebase';
import axios from 'axios';

const Login = ({ toggleDrawer }) => {
  const [lang] = useContext(LangContext);
  const [formMode, setFormMode] = useState("login");
  const [pageTitle, setPageTitle] = useState("Login");
  const [metaDescription, setMetaDescription] = useState(
    "Login to your Techhype account"
  );
  const [loading, setLoading] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [registrationMessage, setRegistrationMessage] = useState(null);
  const navigate = useNavigate();
  const { updateUser, fetchUserData } = useUser();

  function getSchemaForFormMode(formMode) {
    switch (formMode) {
      case "login":
        return loginSchema;
      case "register":
        return registrationSchema;
      case "forgotPassword":
        return forgotPasswordSchema;
      default:
        return loginSchema;
    }
  }

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(getSchemaForFormMode(formMode)),
  });

  const onLoginSubmit = async (data) => {
    try {
      setLoading(true);
      const userCredential = await signInWithEmailAndPassword(auth, data.email, data.password);
      const user = userCredential.user;

      // Optionally, fetch additional user data from your MySQL database here
      await fetchUserData();

      navigate("/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      setRegistrationStatus("fail");
      setRegistrationMessage("Login failed, Incorrect email or password");
    } finally {
      setLoading(false);
    }
  };

  const onRegistrationSubmit = async (data) => {
    try {
      setLoading(true);
  
      // Make a POST request to your backend to register the user
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/signup`, {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        withCredentials: true,
      });
  
      // Registration was successful
      setRegistrationStatus("success");
      setRegistrationMessage(response.data.result || "Registration successful. Please check your email for verification instructions.");
      reset();
      navigate("/dashboard");
    } catch (error) {
      console.error("Registration error:", error);
      setRegistrationStatus("fail");
      setRegistrationMessage(error.response?.data?.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const onForgotPasswordSubmit = async (data) => {
    try {
      setLoading(true);
      await sendPasswordResetEmail(auth, data.email);
      setRegistrationStatus("success");
      setRegistrationMessage(
        "A password reset link has been sent to your email. Please check your inbox."
      );
    } catch (error) {
      console.error("Forgot Password error:", error);
      setRegistrationStatus("fail");
      setRegistrationMessage("Failed to send password reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleFormMode = (mode) => {
    setFormMode(mode);
    reset();
    setRegistrationMessage(null);

    if (mode === "login") {
      setPageTitle("Login");
      setMetaDescription("Login to your Techhype account");
    } else if (mode === "register") {
      setPageTitle("Registration");
      setMetaDescription("Register for a Techhype account");
    } else if (mode === "forgotPassword") {
      setPageTitle("Forgot Password");
      setMetaDescription("Reset your password");
    }
  };

  const forms = {
    login: (
      <LoginForm
        control={control}
        errors={errors}
        lang={lang}
        onSubmit={handleSubmit(onLoginSubmit)}
        onForgotPassword={() => toggleFormMode("forgotPassword")}
        onSignUp={() => toggleFormMode("register")}
        schema={getSchemaForFormMode("login")}
      />
    ),
    register: (
      <RegistrationForm
        control={control}
        errors={errors}
        lang={lang}
        onSubmit={handleSubmit(onRegistrationSubmit)}
        onLogin={() => toggleFormMode("login")}
        schema={getSchemaForFormMode("register")}
      />
    ),
    forgotPassword: (
      <ForgotPasswordForm
        control={control}
        errors={errors}
        lang={lang}
        onSubmit={handleSubmit(onForgotPasswordSubmit)}
        onLogin={() => toggleFormMode("login")}
        schema={getSchemaForFormMode("forgotPassword")}
      />
    ),
  };

  return (
    <Layout
      page={pageTitle}
      description={metaDescription}
      toggleDrawer={toggleDrawer}
    >
      <section className="login top-overlay">
        <div className="container-inner login-container">
          <Paper
            elevation={3}
            sx={{ maxWidth: "550px", margin: "2em auto", borderRadius: "10px" }}
          >
            {loading ? (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Dna visible={true} height={80} width={80} />
              </div>
            ) : (
              forms[formMode]
            )}
            {registrationMessage && (
              <AlertMessage
                variant={
                  registrationStatus === "success" ? "success" : "warning"
                }
                title={
                  registrationStatus === "success"
                    ? "Success!"
                    : "Something went wrong"
                }
                message={registrationMessage}
              />
            )}
          </Paper>
        </div>
      </section>
    </Layout>
  );
};

export default Login;
